import {
	ChangeDetectorRef,
	Component,
	HostListener,
	OnInit,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { openClose } from '@core/animations/animations';
import {
	getDocumentHtmlId,
	getTemplateVersionList,
} from '@core/util/documents';
import { FolderBreadcrumbs } from '@core/util/folder';
import { ICON } from '@core/util/icon';
import { formatHTMLUsingTinyMCEFormatter } from '@core/util/tinymce';
import { DocumentFooters } from '@models/businesses';
import {
	BusinessDocumentV2,
	DocumentV2Html,
	VersionListItem,
} from '@models/documents';
import {
	BusinessDocumentFooterConfiguration,
	DefaultDocumentV2,
	Folder,
	MatterFileType,
} from '@models/matter';
import { DocumentMargins, PageSize } from '@models/documents';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { SelectSnapshot } from '@ngxs-labs/select-snapshot';
import { Select } from '@ngxs/store';
import { BusinessDocumentHtmlRepoService } from '@repos/business-document-html/business-document-html.service';
import {
	BusinessDocumentsDispatch,
	BusinessDocumentsState,
} from '@state/business/documents';
import { BusinessFoldersState } from '@state/business/folders/folders.state';
import { GlobalDocumentsState } from '@state/global-documents';
import { cloneDeep, isEqual } from 'lodash';
import { BehaviorSubject, Observable } from 'rxjs';
import {
	distinctUntilChanged,
	skipWhile,
	switchMap,
	take,
} from 'rxjs/operators';
import { NavigationService } from 'src/app/shared/services/nav/nav.service';
import { PendingChangesDialogComponent } from './pending-changes-dialog/pending-changes-dialog.component';
import { BusinessState } from '@state/business';

@UntilDestroy()
@Component({
	selector: 'app-template-versioning',
	templateUrl: 'template-versioning.component.html',
	styleUrls: ['./template-versioning.component.scss'],
	animations: [openClose(300)],
	standalone: false,
})
export class TemplateVersioningComponent implements OnInit {
	public breadcrumbs: string[];
	public documentId: string;

	public selectedVersion: string;
	public versionsList: VersionListItem[];
	public fileTypes = MatterFileType;
	public fileType: string;

	public showViewer = true;
	public showEditor = true;

	public currentBusinessTemplate: BusinessDocumentV2;
	public currentGlobalTemplate: BusinessDocumentV2;
	public currentSelectedVersionTemplate: BusinessDocumentV2;
	public currentSelectedVersionTemplateHtml: DocumentV2Html;

	public showUpdateToLastestAlert = false;

	public ICON = ICON;
	public showAllVersion = false;
	public saveButtonDisabled = true;
	public showMatterTypes: MatterFileType[];

	public documentHtml: DocumentV2Html;

	@Select(BusinessDocumentsState.getAllMerged)
	private mergedBusinessTemplates$: Observable<BusinessDocumentV2[]>;
	@SelectSnapshot(GlobalDocumentsState.getDocumentBySource)
	private globalDocumentBySource: (source: string) => BusinessDocumentV2;
	@SelectSnapshot(BusinessDocumentsState.getBySource)
	private businessDocumentBySource: (source: string) => BusinessDocumentV2;
	@SelectSnapshot(BusinessFoldersState.getAll) private folders: Folder[];
	@SelectSnapshot(BusinessState.getFooter)
	private footerGlobalConfig: DocumentFooters;

	public currentViewingDocument$: BehaviorSubject<BusinessDocumentV2> =
		new BehaviorSubject<BusinessDocumentV2>(null);

	public savingDocument: BusinessDocumentV2;
	public savingDocumentHtml: DocumentV2Html;

	constructor(
		private activatedRoute: ActivatedRoute,
		private cdr: ChangeDetectorRef,
		private businessDocumentsDispatch: BusinessDocumentsDispatch,
		private navigationService: NavigationService,
		private dialog: MatDialog,
		private businessDocumentHtmlRepoService: BusinessDocumentHtmlRepoService,
	) {
		this.breadcrumbs = [];
		this.activatedRoute.params
			.pipe(untilDestroyed(this))
			.subscribe((params) => {
				this.documentId = params.templateId;
			});

		// get url params
		this.activatedRoute.queryParams.pipe(take(1)).subscribe((params) => {
			this.fileType = params.type;
		});

		this.showMatterTypes = [];
	}

	ngOnInit(): void {
		this.mergedBusinessTemplates$
			.pipe(
				untilDestroyed(this),
				// avoid editor refreshing when someone edits any other template simultaneously
				distinctUntilChanged((a, b) => {
					const currentDoc = a.find(
						(template) => template.source === this.documentId,
					);
					const incomingDoc = b.find(
						(template) => template.source === this.documentId,
					);

					const currentDocHtmlId = currentDoc
						? getDocumentHtmlId(currentDoc)
						: null;
					const incomingDocHtmlId = incomingDoc
						? getDocumentHtmlId(incomingDoc)
						: null;

					return (
						isEqual(currentDocHtmlId, incomingDocHtmlId) &&
						isEqual(currentDoc, incomingDoc)
					);
				}),
			)
			.subscribe(async (businessTemplates) => {
				// Business template
				this.currentBusinessTemplate = businessTemplates.find(
					(template) => template.id === this.documentId,
				);

				if (this.currentBusinessTemplate) {
					// Hardcopy of document to be edited
					this.savingDocument = cloneDeep(
						this.currentBusinessTemplate || DefaultDocumentV2,
					);

					this.savingDocument.footer = this.getFooterConfig(
						this.savingDocument,
						this.footerGlobalConfig,
					);
					// helper objects for ui
					this.breadcrumbs = this.getBreadcrumbs(
						this.savingDocument.folderId,
						this.folders,
					);
					this.showMatterTypes = this.getPossibleMatterTypes(
						this.savingDocument.folderId || this.savingDocument.groupType,
						this.folders,
					);
				}

				// Getting the latest version of the global template
				this.currentGlobalTemplate = this.currentBusinessTemplate?.source
					? this.globalDocumentBySource(this.currentBusinessTemplate.source)
					: null;

				this.versionsList = getTemplateVersionList(
					this.currentGlobalTemplate,
					this.currentBusinessTemplate,
				);

				this.currentViewingDocument$.next(this.currentBusinessTemplate);
			});

		this.currentViewingDocument$
			.pipe(
				untilDestroyed(this),
				skipWhile((document) => !document),
				switchMap((document) => {
					const id = getDocumentHtmlId(document);
					return this.businessDocumentHtmlRepoService.Get(id).pipe(take(1));
				}),
			)
			.subscribe(async (document) => {
				const data = document.data() as DocumentV2Html;

				// Setting the initial document html

				if (data) {
					if (!this.savingDocumentHtml || !this.documentHtml) {
						this.savingDocumentHtml = cloneDeep(data);
						this.documentHtml = cloneDeep(data);
						this.refreshEditor();
					}
				}

				// Check if the incoming HTML is different from the HTML showed in the editor
				if (data?.html !== this.documentHtml?.html) {
					this.savingDocumentHtml = cloneDeep(data);
					this.documentHtml = cloneDeep(data);

					// If I don't do this it stops working idk, send help please
					this.refreshEditor();
				}
			});
	}

	getBreadcrumbs(folderId: string, folders: Folder[]): string[] {
		return FolderBreadcrumbs(folderId, folders);
	}

	getPossibleMatterTypes(
		folderId: string,
		folders: Folder[],
	): MatterFileType[] {
		return folders.find((folder) => folder.id === folderId)?.matterType || [];
	}

	@HostListener('window:beforeunload')
	async canDeactivate(): Promise<boolean> {
		if (!this.saveButtonDisabled) {
			const dialog = this.dialog.open(PendingChangesDialogComponent, {
				width: '400px',
				disableClose: true,
			});
			const result = await dialog.afterClosed().pipe(take(1)).toPromise();

			if (result) {
				this.saveDocument();
			}
		}

		return true;
	}

	async onSelectVersion(versionItem: VersionListItem) {
		this.selectedVersion = versionItem.version;
		this.currentSelectedVersionTemplate = {
			...(versionItem.baseDocument as BusinessDocumentV2),
			parentVersion: versionItem.versionItem.parentVersion,
			version: versionItem.versionItem.version,
		};
		this.currentSelectedVersionTemplateHtml = (
			await this.businessDocumentHtmlRepoService
				.Get(getDocumentHtmlId(this.currentSelectedVersionTemplate))
				.pipe(take(1))
				.toPromise()
		).data() as DocumentV2Html;

		const nextCurrentViewingDocument = {
			...(versionItem.baseDocument as BusinessDocumentV2),
			parentVersion: versionItem.versionItem.parentVersion,
			version: versionItem.versionItem.version,
		};
		this.currentViewingDocument$.next(nextCurrentViewingDocument);

		// force rerender of the editor to get the new version of the document
		// TODO: find a better way of doing this
		this.showViewer = false;
		this.cdr.detectChanges();
		this.showViewer = true;
	}

	goBackToCurrentDocument() {
		this.currentViewingDocument$.next(this.currentBusinessTemplate);
		this.selectedVersion = null;
		this.currentSelectedVersionTemplate = null;
		this.currentSelectedVersionTemplateHtml = null;
	}

	updateToLastestDocument() {
		this.businessDocumentsDispatch.UpdateToLastestAndArchiveCurrentDocument(
			this.documentId,
		);
	}

	rollBackToOldDocument() {
		this.businessDocumentsDispatch.RollBackToOldAndArchiveCurrentDocument({
			currentVersion: this.currentBusinessTemplate,
			newVersion: this.currentSelectedVersionTemplate,
		});
		this.selectedVersion = null;
	}

	saveDocumentMetaData() {
		this.businessDocumentsDispatch.UpdateDocumentMetaData(this.savingDocument);
	}

	saveDocument() {
		if (this.savingDocument && !this.saveButtonDisabled) {
			if (this.hasDocumentHtmlChanged() || this.hasDocumentChanged()) {
				this.businessDocumentsDispatch.SaveAndArchive({
					new: {
						document: this.savingDocument,
						documentHtml: this.savingDocumentHtml,
					},
					old: {
						document: this.currentBusinessTemplate,
						documentHtml: this.documentHtml,
					},
				});
			}

			this.saveButtonDisabled = true;
		}
	}

	goBack() {
		this.navigationService.goToBusinessManageTemplates(this.fileType);
	}

	toggleShowAllVersions() {
		this.showAllVersion = !this.showAllVersion;
	}

	onDocumentMarginsAndPageSizeChange(settings: {
		margins: DocumentMargins;
		pageSize: PageSize;
	}) {
		this.savingDocument.margins = settings.margins;
		this.savingDocument.pageSize = settings.pageSize;
		this.toggleSaveButton();
	}

	onTitleChange(title: string) {
		this.savingDocument.title = title;
		this.toggleSaveButton();
	}

	onMarginsChange(margins: DocumentMargins) {
		this.savingDocument.margins = margins;
		this.toggleSaveButton();
	}

	onPageSizeChange(pageSize: PageSize) {
		this.savingDocument.pageSize = pageSize;
		this.toggleSaveButton();
	}

	onDocumentHtmlChange(documentHtml: string) {
		this.savingDocumentHtml.html = documentHtml;
		this.toggleSaveButton();
	}

	onMatterTypesUpdate(matterTypes: MatterFileType[]) {
		this.savingDocument.matterType = matterTypes;
		this.toggleSaveButton();
	}

	onAlwaysExistUpdate(alwaysExist: boolean) {
		this.savingDocument.alwaysExists = alwaysExist;
		this.toggleSaveButton();
	}

	onFooterOptionsUpdate(footer: BusinessDocumentFooterConfiguration) {
		this.savingDocument.footer = footer;
		this.toggleSaveButton();
	}

	onHeaderOptionsUpdate(headerEnabled: boolean) {
		this.savingDocument.headerEnabled = headerEnabled;
		this.toggleSaveButton();
	}

	toggleSaveButton() {
		const SavingDocumentHtml = cloneDeep(this.savingDocumentHtml);
		SavingDocumentHtml.html = formatHTMLUsingTinyMCEFormatter(
			SavingDocumentHtml.html,
		);

		const BusinessDocumentHtml = cloneDeep(this.documentHtml);

		BusinessDocumentHtml.html = formatHTMLUsingTinyMCEFormatter(
			BusinessDocumentHtml.html,
		);

		this.saveButtonDisabled =
			!this.hasDocumentChanged() && !this.hasDocumentHtmlChanged();
	}

	hasDocumentChanged(): boolean {
		return !isEqual(this.savingDocument, this.currentBusinessTemplate);
	}

	hasDocumentHtmlChanged(): boolean {
		const SavingDocumentHtml = cloneDeep(this.savingDocumentHtml);
		SavingDocumentHtml.html = formatHTMLUsingTinyMCEFormatter(
			SavingDocumentHtml.html,
		);

		const BusinessDocumentHtml = cloneDeep(this.documentHtml);

		BusinessDocumentHtml.html = formatHTMLUsingTinyMCEFormatter(
			BusinessDocumentHtml.html,
		);

		return SavingDocumentHtml.html !== BusinessDocumentHtml.html;
	}

	getFooterConfig(
		doc: BusinessDocumentV2,
		defaultFooterConfig: DocumentFooters,
	): BusinessDocumentFooterConfiguration {
		const firstPageFooter =
			doc?.footer?.firstPage?.enabled ??
			defaultFooterConfig?.first?.enabled ??
			true;
		const allPagesFooter =
			doc?.footer?.allPages?.enabled ??
			defaultFooterConfig?.global?.enabled ??
			true;

		return {
			firstPage: { enabled: firstPageFooter },
			allPages: { enabled: allPagesFooter },
		};
	}

	refreshEditor() {
		this.showEditor = false;
		this.cdr.detectChanges();
		this.showEditor = true;
	}
}
